import type { DraggableSyntheticListeners } from '@dnd-kit/core';
import clsx from 'clsx';
import type React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { AtomButton, Tooltip } from '@gitbook/spine-react';

import { useColumnOffset } from '../utils/useColumnOffset';

interface ColumnDragHandleProps {
    listeners: DraggableSyntheticListeners;
    isDragging: boolean;
    columnId: string;
    columnOrder: string[];
    columnWidth: number;
}

export function ColumnDragHandle({
    listeners,
    isDragging,
    columnId,
    columnOrder,
    columnWidth,
}: ColumnDragHandleProps) {
    const columnOffset = useColumnOffset({ columnId, columnOrder });

    // Calculates where the knob should be positioned relative to the _table_, as we can't set the header cells to be relative or the vertical resizer can't span the entire height of the table.
    const leftPosition = columnOffset + columnWidth / 2;

    const label = 'Drag to move column';

    return (
        <Tooltip data-exclude-from-copy label={label} disabled={isDragging}>
            <AtomButton
                data-exclude-from-copy
                {...listeners}
                style={
                    {
                        '--knob-bg-default': 'var(--sp-color-bg-table-drag-button-default)',
                        '--knob-bg-active': 'var(--sp-color-bg-table-drag-button-active)',
                        // Position at the center of the column
                        left: `${leftPosition}px`,
                    } as React.CSSProperties
                }
                className={clsx(
                    'pointer-events-auto',
                    'group/columnHandle absolute top-[6px] z-20 flex h-[5px] w-6 shrink-0 items-center justify-center rounded-[5px] opacity-0 transition-all',
                    // Default styles, a slim 'knob'
                    'py-0.5',
                    // Entire column is hovered
                    'group-focus-within/tablecolumn:opacity-100 group-hover/tablecolumn:opacity-100',
                    // When knob is hovered or focused, knob expands and shows icon
                    'hover:top-[3px] hover:bg-[var(--knob-bg-active)] hover:py-1.5',
                    // When knob is selected (incl. onMouseDown)
                    'focus:bg-[var(--knob-bg-active)] focus:shadow-focus-ring active:top-[3px] active:scale-95 active:bg-[var(--knob-bg-active)] active:shadow-focus-ring',
                    isDragging ? 'hidden cursor-grabbing' : 'top-[6px] bg-[var(--knob-bg-default)]',
                    'active:cursor-grab'
                )}
            >
                <Icon.MoreHorizontal
                    size={14}
                    aria-hidden
                    className={clsx(
                        'pointer-events-none shrink-0 select-none text-inverted-standout transition-opacity',
                        isDragging
                            ? 'opacity-100'
                            : 'opacity-0 delay-100 group-hover/columnHandle:opacity-100'
                    )}
                />
            </AtomButton>
        </Tooltip>
    );
}
